// Pure date parsing/formatting for <DateField>. Kept free of React so it is
// cheap to test. The stored format is always ISO "YYYY-MM-DD" (what the
// native <input type="date"> used to give every caller), so swapping the
// control changes how a date is ENTERED, never what is saved.

const pad = (n: number): string => String(n).padStart(2, "0");

/** True when y-m-d is a real calendar day (rejects 02/30, month 13, ...). */
function isRealDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || y < 1000 || y > 9999) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** "2026-07-12" -> "07/12/2026"; anything else -> "". */
export function isoToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
}

/**
 * Accepts what people actually type: 7/12/2026, 07-12-26, 7.12.26,
 * 07122026, 2026-07-12. Two-digit years mean 20xx (this is a working-dates
 * tool, and "26" meaning 1926 would be a silent wrong answer).
 * Returns ISO, or null when it is not a real date.
 */
export function parseTypedDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;

  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return isRealDate(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
  }

  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(s);
  if (!m) {
    // Digits only: MMDDYYYY or MMDDYY.
    m = /^(\d{2})(\d{2})(\d{4}|\d{2})$/.exec(s);
    if (!m) return null;
  }
  const mo = Number(m[1]);
  const d = Number(m[2]);
  const y = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return isRealDate(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null;
}
